import type { CommandAskDto, CommandAskInputDto, CommandEntryInputDto } from '@pams/shared';
import { commandAskIsReal } from '@/pages/ArrangementBuilder/autosave';
import { mintShiftId } from './draft';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a §2] A COMMAND ROLE ASKED OF A DIVISION — THE BUILDER'S HALF, IN ONE MODULE.
 *
 *  The wire carries an ask as ONE ROW PER POSITION (`commandSeat.schema.ts` records why: a
 *  command position is never a quantity in the API). The builder edits it as rows of
 *  (division + optional unit, how many), because that is how an operator says "two from
 *  Central" — so this file is the one crossing between the two shapes, in both directions.
 *
 *  ⚠️ THE NUMBER TYPED HERE NEVER LEAVES THE BUILDER AS A NUMBER. `expandAskRows` turns it into
 *  that many position rows before anything is written, and nothing on this page adds a position
 *  to a force figure — the card header's "N in command" counts NAMED officers only
 *  (`DutyPointCard.tsx`), and `willCreate` has no command parameter at all (FR-5.2.6b).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ A POSITION'S IDENTITY, MINTED FROM RANDOMNESS — `mintDutyPointId`'s rule and the same
 * `getRandomValues`, never `randomUUID`, which is secure-context only and invisible to every test
 * (CLAUDE.md §8, divergence #28). Once issued, a seat is keyed on it; a derived or positional id
 * would re-point a division's answer the moment a row moved.
 */
export const mintAskId = (): string => mintShiftId();

/**
 * ⚠️ THE CONTENT PREDICATES ARE NOT HERE — `commandAskIsReal` and `commandEntryHasContent` live
 * beside `instructionIsReal` in `pages/ArrangementBuilder/autosave.ts`, where [C5c §8e] states the
 * rule they obey (and where importing them creates no cycle through `draft.ts`). The first is
 * re-exported so the section needs one import for everything about an ask.
 */
export { commandAskIsReal };

/**
 * The card's entries as the wire keeps them: every half-built ask dropped, through the ONE
 * predicate. An entry with no `asks` key keeps none, so a by-name card is sent exactly as it was
 * before C6a.
 */
export const submittableCommandEntries = (entries: readonly CommandEntryInputDto[]): CommandEntryInputDto[] =>
  entries.map((entry) => (entry.asks === undefined ? entry : { ...entry, asks: entry.asks.filter(commandAskIsReal) }));

// ── The editor's target: division + optional unit, as one opaque key ─────────────────

/**
 * ⚠️ `division|unit`, with an EMPTY unit meaning "any unit under this division" — `SourceCell`'s
 * own `ANY_UNIT` convention, so the two halves of the target travel as one value through
 * `DelegationRowsEditor`, which knows nothing about either.
 */
export const askTargetKey = (divisionOrgUnitId: string, unitOrgUnitId: string | null | undefined): string =>
  `${divisionOrgUnitId}|${unitOrgUnitId ?? ''}`;

export const askTargetOf = (key: string | null): { divisionOrgUnitId: string; unitOrgUnitId: string | null } => {
  const [division = '', unit = ''] = (key ?? '').split('|');
  return { divisionOrgUnitId: division, unitOrgUnitId: unit === '' ? null : unit };
};

/**
 * ⚠️ THE STORED POSITIONS, GROUPED BACK INTO ROWS — the editor's SEED. First appearance decides
 * the order, so re-opening a confirmed ask shows the rows the operator typed, in that order.
 */
export const askRowsOf = (asks: readonly CommandAskInputDto[] | undefined): { target: string; count: number }[] => {
  const rows = new Map<string, number>();
  for (const ask of (asks ?? []).filter(commandAskIsReal)) {
    const key = askTargetKey(ask.divisionOrgUnitId, ask.unitOrgUnitId);
    rows.set(key, (rows.get(key) ?? 0) + 1);
  }
  return [...rows].map(([target, count]) => ({ target, count }));
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ CONFIRM: EACH ROW'S NUMBER BECOMES THAT MANY POSITIONS.
 *
 *  ⚠️ A POSITION THAT SURVIVES AN EDIT KEEPS ITS ID. The existing ids of a target are reused
 *  first, in order, and only the positions beyond them are minted — so changing "two from
 *  Central" to "three" keeps the first two identities and adds one. On a DRAFT nothing is keyed
 *  on them yet; keeping them is still the cheaper truth, and it is what makes an unchanged
 *  confirm a no-op on the wire.
 *
 *  A row with no division is dropped (it is not an ask), and so is a row asking for none.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const expandAskRows = (
  rows: readonly { target: string | null; count: number }[],
  existing: readonly CommandAskInputDto[] | undefined,
): CommandAskInputDto[] => {
  const idsByTarget = new Map<string, string[]>();
  for (const ask of (existing ?? []).filter(commandAskIsReal)) {
    if (!ask.askId) continue;
    const key = askTargetKey(ask.divisionOrgUnitId, ask.unitOrgUnitId);
    idsByTarget.set(key, [...(idsByTarget.get(key) ?? []), ask.askId]);
  }
  return rows.flatMap((row) => {
    const target = askTargetOf(row.target);
    if (target.divisionOrgUnitId === '' || row.count < 1) return [];
    const key = askTargetKey(target.divisionOrgUnitId, target.unitOrgUnitId);
    return Array.from({ length: row.count }, () => {
      const kept = idsByTarget.get(key)?.shift();
      return {
        askId: kept ?? mintAskId(),
        divisionOrgUnitId: target.divisionOrgUnitId,
        unitOrgUnitId: target.unitOrgUnitId,
      };
    });
  });
};

/**
 * WHO A ROLE IS ASKED OF, AS DISTINCT ADDRESSEES IN FIRST-APPEARANCE ORDER, each with how many
 * positions are asked of it. The builder states the addressees in words and a figure ONLY where
 * one addressee is asked for more than one position — on its own line, never beside a force number.
 */
export const askedAddressees = (
  asks: readonly CommandAskInputDto[] | undefined,
): { divisionOrgUnitId: string; unitOrgUnitId: string | null; positions: number }[] =>
  askRowsOf(asks).map((row) => ({ ...askTargetOf(row.target), positions: row.count }));

/**
 * ⚠️ [D4.3] THE WEB CARD DUPLICATE, FOR COMMAND — a DEEP copy with every position RE-MINTED.
 *
 * `commandEntriesInputSchema` refuses a repeated ask id on one duty point, and once issued a
 * seat is keyed on it — two cards sharing one would be one identity asked twice. `perSlot` is
 * dropped for the reason `duplicateDutyPoint` states (a date-keyed override is orphaned when the
 * schedule re-anchors); the officers and the asks are the click-saver and travel.
 */
export const duplicateCommandEntries = (entries: readonly CommandEntryInputDto[]): CommandEntryInputDto[] =>
  entries.map((entry) => ({
    ...entry,
    officerIds: [...entry.officerIds],
    perSlot: [],
    ...(entry.asks === undefined ? {} : { asks: entry.asks.map((ask) => ({ ...ask, askId: mintAskId() })) }),
  }));

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ THE RECORD'S ASKED POSITIONS, PER DUTY POINT AND THEN BY ASK ID — never by ask id alone.
 *
 *  An ask id is unique on its DUTY POINT only: `commandEntriesInputSchema` refuses a repeat on one
 *  card, the `one_seat_per_ask` index is `(arrangement_id, duty_point_id, ask_id)`, and `toDto`
 *  keys its own lookup `${dutyPointId}#${askId}`. Two cards may legally hold the same id (an API
 *  client is free to send it), and one map across the order would then show card A's seat and
 *  officer on card B's read-only view. The page hands each card the inner map of its own id.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const commandAsksByDutyPoint = (
  points: readonly { id: string; commandEntries: readonly { asks?: readonly CommandAskDto[] | undefined }[] }[],
): Map<string, Map<string, CommandAskDto>> =>
  new Map(
    points.map((point) => [
      point.id,
      new Map(point.commandEntries.flatMap((entry) => (entry.asks ?? []).map((ask) => [ask.askId, ask] as const))),
    ]),
  );
